// HTTP client for the external admin API.
//
// The admin site is UI-only — it hosts no API of its own and calls an
// external admin API whose base URL is configured per environment via
// VITE_API_URL (see the .env.* files). A VITE_API_URL pipeline
// environment variable overrides the .env value at build time, so each
// deployed environment points at its own API endpoint.
//
// Auth: the SWA auth id_token from /.auth/me is attached to every
// request as a Bearer token, which the external API verifies against
// the shared Entra ID tenant. The API must allow the admin origins in
// its CORS policy (docs/plans/admin-site-proposal.md, section 10).

import axios from 'axios';
// The peer-environment admin token (browser-local secret). Sent per-request
// so cross-environment API calls authenticate against the OTHER environment.
import { clearStoredToken, loadStoredToken, loadPeerToken } from '../utils/tokenStore';

// Use an explicitly configured API in every environment. When VITE_API_URL
// is empty, development requests fall back to Vite's same-origin proxy.
export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const stored = loadStoredToken();
  if (stored?.token) {
    config.headers.Authorization = `Bearer ${stored.token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    // A 401, or a 403 that explicitly means "not authenticated", means
    // the token is dead. A plain 403 from a valid admin is a permission
    // denial and must NOT be mistaken for a lost session (that would
    // kick a valid admin to the sign-in screen for a data permission issue).
    const detail =
      (error?.response?.data?.detail ||
        error?.response?.data?.message ||
        '').toString();
    const sessionLost =
      status === 401 || (status === 403 && /not authenticated/i.test(detail));
    if (status === 401 || status === 403) {
      // Drop the cached token so the next request re-authenticates.
      clearStoredToken();
    }
    if (sessionLost) {
      // Tell the auth context the session is dead so it drops the
      // in-memory profile; ProtectedRoute then shows the sign-in
      // screen instead of an empty layout.
      window.dispatchEvent(new Event('journiy:admin-token-cleared'));
    }
    return Promise.reject(error);
  },
);

/** Build per-request headers carrying the browser-stored peer token. */
export function peerTokenHeaders() {
  const token = loadPeerToken();
  return token ? { 'X-Image-Sync-Peer-Token': token } : {};
}

/** Map a thrown error to a short, user-presentable message. */
export function toErrorMessage(err, fallback = 'Something went wrong') {
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.response?.status) return `Request failed with status ${err.response.status}`;
  if (err?.code === 'ECONNABORTED') return 'The request timed out';
  if (err?.message === 'Network Error') return 'Could not reach the API backend';
  return err?.message || fallback;
}

// ── Admin API surface ─────────────────────────────────────────────────
// Endpoint paths follow the /admin/* convention on the API backend
// (docs/plans/admin-site-proposal.md, phase 4 — API integration). The
// backend endpoints are provisioned outside this SPA; until they exist
// these wrappers fail and the pages render their "API unavailable"
// states. Responses are expected to be either a bare array or an
// envelope of the shape { items: [...], total: number }.

export const getVerificationEvents = ({ page = 1, pageSize = 20, search = '', result = '' } = {}) =>
  api
    .get('/admin/verifications', {
      params: { page, pageSize, search: search || undefined, result: result || undefined },
    })
    .then((r) => r.data);

export const getUsers = ({ page = 1, pageSize = 20, search = '', user_ids = '', role = '', premium = undefined } = {}) =>
  api
    .get('/api/userprofiles/admin/users', {
      params: {
        page,
        page_size: pageSize,
        search: search || undefined,
        user_ids: user_ids || undefined,
        role: role || undefined,
        premium: premium === undefined ? undefined : String(premium),
      },
    })
    .then((r) => r.data);

// Admin edit of a single user profile (email / notes / premium).
export const updateUserProfile = ({ id, email, notes, premium }) =>
  api
    .put('/api/userprofiles/admin/update', { id, email, notes, premium })
    .then((r) => r.data);

// Admin delete of a user profile (admin role required server-side).
export const deleteUserProfile = (id) =>
  api.delete('/api/userprofiles', { params: { id } }).then((r) => r.data);

// Admin full-delete / GDPR anonymize of one user, including every record
// linked to it (user meals, ingredients, saved meals, BI events).
// Level-2 admin required server-side. mode: 'delete' | 'anonymize'.
export const adminUserPrivacy = (userId, mode) =>
  api
    .post('/api/userprofiles/admin/privacy', { user_id: userId, mode })
    .then((r) => r.data);

export const getCurrentUserProfile = (userId) =>
  api.get('/api/userprofiles', { params: { id: userId } }).then((r) => {
    const profiles = Array.isArray(r.data) ? r.data : [r.data];
    return profiles[0] || null;
  });

/**
 * Fetch aggregate event analytics for the BI page.
 *
 * Calls `GET /api/business-intelligence/events`. Pass the window/granularity
 * params in `params`, e.g. `{ d: 7, granularity: 'daily' }` for a trailing
 * lookback or `{ from: 'YYYY-MM-DD', to: 'YYYY-MM-DD', granularity }` for an
 * explicit window (from inclusive, to exclusive). `user_id` (when set) scopes
 * every metric to that profile.
 */
export const getBiEvents = (params = {}) =>
  api
    .get('/api/business-intelligence/events', {
      params: {
        mode: 'agg',
        ...params,
      },
    })
    .then((r) => r.data);

// ── Restaurants & data management ────────────────────────────────────
// Server-side filtered, paginated restaurant search plus the family
// (root + children + meals) view and cascade delete. Backed by the new
// admin-only /api/restaurants/admin/* endpoints alongside the existing
// PUT/DELETE restaurant and meal routes.

export const searchRestaurants = ({
  name = '',
  state = '',
  city = '',
  zip = '',
  parentsOnly = true,
  page = 1,
  pageSize = 20,
} = {}) =>
  api.get('/api/restaurants/admin/search', {
    params: {
      ...(name?.trim() ? { name: name.trim() } : {}),
      ...(state?.trim() ? { state: state.trim() } : {}),
      ...(city?.trim() ? { city: city.trim() } : {}),
      ...(zip?.trim() ? { zip: zip.trim() } : {}),
      parents_only: parentsOnly,
      page,
      page_size: pageSize,
    },
  })
    .then((r) => r.data);

export const getRestaurantFamily = (restaurantId) =>
  api
    .get('/api/restaurants/admin/details', { params: { restaurant_id: restaurantId } })
    .then((r) => r.data);

export const deleteRestaurantCascade = (restaurantId) =>
  api.delete('/api/restaurants/admin/cascade', { params: { restaurant_id: restaurantId } }).then((r) => r.data);

export const putRestaurant = (data) => api.put('/api/restaurants', data).then((r) => r.data);

export const putMeal = (data) => api.put('/api/meals', data).then((r) => r.data);

export const deleteRestaurant = (id) =>
  api.delete('/api/restaurants', { params: { id } }).then((r) => r.data);

export const deleteMeal = (id) => api.delete('/api/meals', { params: { id } }).then((r) => r.data);

// ── Notifications ────────────────────────────────────────────────────
// Admin query + status management for the four submission record types
// (geo-fence waitlist, waitlist, account-delete, support tickets).
// Backed by the admin-only /api/notifications/admin/* endpoints.
//
// Workflow states: pending, in_progress, completed, cancelled, failed.
// Typical lifecycle: pending -> in_progress -> completed.

export const searchNotifications = ({
  kind,
  q = '',
  states = [],
  page = 1,
  pageSize = 20,
} = {}) =>
  api
    .get('/api/notifications/admin/search', {
      params: {
        kind,
        ...(q?.trim() ? { q: q.trim() } : {}),
        ...(states?.length ? { states } : {}),
        page,
        page_size: pageSize,
      },
      // FastAPI expects repeated params (states=a&states=b); axios's
      // default emits bracket form (states[]=a) which the API ignores.
      paramsSerializer: (params) => {
        const sp = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const item of value) sp.append(key, item);
          } else {
            sp.append(key, value);
          }
        }
        return sp.toString();
      },
    })
    .then((r) => r.data);

export const updateNotificationState = (kind, ids, state) =>
  api
    .post('/api/notifications/admin/update_state', { kind, ids, state })
    .then((r) => r.data);

// ── Data onboarding ──────────────────────────────────────────
// Resumable restaurant/meal/image import pipeline. The import call parses
// the workbook and runs the full validation sweep, storing a resumable run;
// /process advances one phase per call (restaurants -> meals -> images);
// /rollback undoes everything the run created.
//
// Meal-image management for the data-management edit view: the API owns
// blob storage (the SPA never touches the CDN directly).

export const importOnboardingWorkbook = (workbookFile, imageFiles = []) => {
  const form = new FormData();
  form.append('workbook', workbookFile);
  for (const file of imageFiles) form.append('images', file);
  return api
    .post('/api/onboarding/import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
    .then((r) => r.data);
};

export const getOnboardingRun = (runId) =>
  api.get(`/api/onboarding/${runId}`).then((r) => r.data);

export const processOnboardingRun = (runId) =>
  api.post(`/api/onboarding/${runId}/process`, null, { timeout: 180000 }).then((r) => r.data);

export const rollbackOnboardingRun = (runId) =>
  api.post(`/api/onboarding/${runId}/rollback`, null, { timeout: 180000 }).then((r) => r.data);

export const uploadMealImage = (mealId, file) => {
  const form = new FormData();
  form.append('image', file);
  return api
    .post(`/api/onboarding/meal-image/${mealId}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 90000,
    })
    .then((r) => r.data);
};

export const removeMealImage = (mealId) =>
  api.delete(`/api/onboarding/meal-image/${mealId}`).then((r) => r.data);

// Template workbook (both sheets + headers) for download.
export const downloadOnboardingTemplate = () =>
  api.get('/api/onboarding/template', { responseType: 'blob' }).then((r) => r.data);

// Orphaned-image tool: scan the image container for blobs no record claims.
export const scanOrphanImages = () =>
  api.get('/api/onboarding/tools/scan-orphan-images').then((r) => r.data);

// Delete specific orphaned image blobs (re-verified server-side as orphans).
// kind: 'orphan' (default) or 'staging' (blobs of runs whose doc is gone).
export const deleteOrphanImages = (names, kind = 'orphan') =>
  api.post('/api/onboarding/tools/delete-orphan-image', { names, kind }).then((r) => r.data);

// ── Image sync (cross-environment) ────────────────────────────────────
// Bidirectional restaurant/meal image sync (prod<->bvt) driven from the
// admin portal. A resumable run lives server-side: create -> scan ->
// upload (batched, checkpointed) -> reset. The SPA never touches the CDN
// or blob storage directly — the API does all data movement.
//
// Bulk meal-image upload (validate -> review -> upload wizard): the same
// endpoint serves validation (?validate_only=true) and the commit step.

export const createImageSyncRun = ({ direction, kinds = ['restaurants', 'meals'], force = false, replace = false, restaurantId }) =>
  api
    .post('/api/image-sync/runs', {
      ...(direction ? { direction } : {}),
      kinds,
      force,
      replace,
      ...(restaurantId ? { restaurant_id: restaurantId } : {}),
    }, { timeout: 60000 })
    .then((r) => r.data);

export const getImageSyncPeerInfo = () =>
  api.get('/api/image-sync/peer-info').then((r) => r.data);

export const listImageSyncRuns = () =>
  api.get('/api/image-sync/runs').then((r) => r.data);

export const getImageSyncRun = (runId) =>
  api.get(`/api/image-sync/runs/${runId}`).then((r) => r.data);

export const scanImageSyncRun = (runId) =>
  api.post(`/api/image-sync/runs/${runId}/scan`, null, {
    timeout: 300000,
    headers: peerTokenHeaders(),
  }).then((r) => r.data);

export const uploadImageSyncRun = (runId) =>
  api.post(`/api/image-sync/runs/${runId}/upload`, null, { timeout: 300000 }).then((r) => r.data);

// Reset: a state change on the run resource (clears plan/state).
export const resetImageSyncRun = (runId) =>
  api
    .patch(`/api/image-sync/runs/${runId}`, null, { timeout: 60000 })
    .then((r) => r.data);

// Delete: removes the run document entirely (204).
export const deleteImageSyncRun = (runId) =>
  api
    .delete(`/api/image-sync/runs/${runId}`, { timeout: 60000 })
    .then((r) => r.data || null);

// Validate-only step of the bulk meal-image upload wizard.
export const validateBulkMealImages = (files) => {
  const form = new FormData();
  for (const file of files) form.append('images', file);
  return api
    .post('/api/image-sync/meal-images/bulk?validate_only=true', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    })
    .then((r) => r.data);
};

// Commit step: uploads the validated images and flips has_image.
export const uploadBulkMealImages = (files) => {
  const form = new FormData();
  for (const file of files) form.append('images', file);
  return api
    .post('/api/image-sync/meal-images/bulk', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    })
    .then((r) => r.data);
};
